import React, { useEffect, useState, useContext } from "react";
import { useFormContext } from "react-hook-form";
import { Row, Col, Button, Badge, Spinner } from "reactstrap";
import Head from "../../../layout/head/Head";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Descriptions,
  Modal,
  Popover,
  Empty,
  Tag,
  Space,
  Tooltip,
  Timeline,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { formatDecimal } from "../../../utils/Utils";
import workBoqApis from "../../../api/WorkOrderManagement/WorkBOQ";
import { message } from "antd";
import { Icon } from "../../../components/Component";
import { EditModeContext } from "../workBoqCreationMain";

interface SelectOptions {
  label: string;
  value: string;
}

interface AllocatedMaterial {
  id: string;
  materialId: string;
  materialName: string;
  quantity: number;
  uom: string;
  uomName?: string;
  category?: string;
  notes?: string;
}

const { Panel } = AntCollapse;

const WorkBoqPreview: React.FC = (props: any) => {
  const navigate = useNavigate();
  const { handleSubmit, watch, getValues } = useFormContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const { isEditMode, editWorkBoqId, originalData } = useContext(EditModeContext);

  const formData = watch();

  const workLevelLabels: { [key: string]: string } = {
    Project: "Project Level",
    Tower: "Tower Level",
    Villa: "Villa Level",
    Floor: "Floor Level",
    Flat: "Flat Level",
  };

  const completionAlertLabel = formData.completionAlertsFrom
    ? `${formData.completionAlertsFrom}%`
    : "Not Set";

  // Work BOQ Lines table columns
  const [columns, setColumns] = useState<TableColumnsType<any>>([
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 150,
      fixed: "left",
      render: (text, record) => (
        <span>{record.workCategoryName || record.workCategory}</span>
      ),
    },
    // {
    //   title: "Sub Category",
    //   dataIndex: "workSubCategory",
    //   key: "workSubCategory",
    //   width: 150,
    //   render: (text, record) => record.workSubCategoryName || text || "-",
    // },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (text, record) =>
        `${formatDecimal(text)} ${record?.uomName || record?.uom}`,
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      hidden: !["Tower", "Villa", "Floor", "Flat"].includes(formData.workLevel),
      render: (text, record) => {
        const location = [];
        if (record.towerVilla) location.push(record.towerVilla);
        if (record.floorName) location.push(record.floorName);
        if (record.flatName) location.push(record.flatName);
        return location.join(" / ") || "-";
      },
    },
    {
      title: "Materials",
      key: "materials",
      width: 140,
      render: (text, record) =>
        record.allocatedMaterials?.length > 0 ? (
          <Tooltip
            title={`${record.allocatedMaterials.length} materials allocated`}
          >
            <Space>
              <Icon name="check-circle" className="text-success" />
              <span>{record.allocatedMaterials.length} items</span>
            </Space>
          </Tooltip>
        ) : (
          <Space>
            <span>0 items</span>
            <Icon name="cross-circle" className="text-danger" />
          </Space>
        ),
    },
    {
      title: "Budget",
      key: "budget",
      width: 120,
      render: (text, record) => (
        <div>
          {record.budgetLineName ? (
            <Tooltip title={`Budget Line: ${record.budgetLineName}\nAllocated: ₹${formatDecimal(record.allocatedAmount || 0)}`}>
              <Space direction="vertical" size={1}>
                <Tag color="green" style={{ fontSize: "10px", margin: 0 }}>
                  {record.budgetLineName}
                </Tag>
                <span style={{ fontSize: "11px", color: "#666" }}>
                  ₹{formatDecimal(record.allocatedAmount || 0)}
                </span>
              </Space>
            </Tooltip>
          ) : (
            <span style={{ color: "#999", fontSize: "11px" }}>No budget</span>
          )}
        </div>
      ),
    },
    {
      title: "Docs",
      key: "docs",
      width: 80,
      render: (text, record) =>
        record?.uploadedDocs?.length > 0 ? (
          <Tooltip title={`${record.uploadedDocs.length} documents uploaded`}>
            <Space>
              <Icon name="check-circle" className="text-success" />
              <Badge color="success" pill>
                {record.uploadedDocs.length}
              </Badge>
            </Space>
          </Tooltip>
        ) : (
          <Tooltip title="No documents uploaded">
            <Icon name="cross-circle" className="text-danger" />
          </Tooltip>
        ),
    },
  ]);

  // Approval Timeline Content Component
  const ApprovalTimelineContent = () => {
    if (formData.approvalRequired !== "true") {
      return <Badge color="outline-danger">No</Badge>;
    }

    let approvalsData = [];
    try {
      if (formData.approvalsData) {
        const parsedApprovals = JSON.parse(formData.approvalsData);
        approvalsData = parsedApprovals.data || [];
      }
    } catch (error) {
      console.log("Error parsing approvals data:", error);
    }

    if (!approvalsData || approvalsData.length === 0) {
      return <Badge color="outline-warning">Not Set</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              return (
                <Timeline.Item key={index}>
                  <div style={{ fontWeight: "500" }}>
                    {approval?.userName}
                  </div>
                  <div
                    className="designation"
                    style={{ fontSize: "12px", color: "#666" }}
                  >
                    {approval?.roleName}
                  </div>
                  {approval?.approvalStage && (
                    <div style={{ fontSize: "11px", marginTop: "2px" }}>
                      <Tag
                        color={
                          approval.approvalStage === "Approver"
                            ? "green"
                            : approval.approvalStage === "Checker"
                            ? "blue"
                            : "orange"
                        }
                      >
                        {approval.approvalStage}
                      </Tag>
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            color: "#2263b3",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          <Badge color="outline-success">Yes</Badge>
        </span>
      </Popover>
    );
  };

  // Helper function to detect changed fields
  const detectChanges = (original: any, current: any) => {
    const changes: any = {};
    const fieldsToCheck = [
      'boqTitle', 'boqDescription', 'siteId', 'projectId', 'contractorId', 'budgetId',
      'completionAlertsFrom', 'materialBoqId', 'workLevel', 'approvalRequired', 'approvalsData'
    ];

    fieldsToCheck.forEach(field => {
      if (original[field] !== current[field]) {
        changes[field] = current[field];
      }
    });

    return changes;
  };

  // Helper function to detect line changes and deletions
  const detectLineChanges = (originalLines: any[], currentLines: any[]) => {
    const changedLines: any[] = [];
    const newLines: any[] = [];
    const deletedLineIds: string[] = [];

    // Create maps for easier comparison
    const originalMap = new Map();
    originalLines?.forEach(line => {
      if (line.id) {
        originalMap.set(line.id, line);
      }
    });

    const currentMap = new Map();
    currentLines?.forEach(line => {
      if (line.id) {
        currentMap.set(line.id, line);
      }
    });

    // Check for deleted lines
    originalLines?.forEach(originalLine => {
      if (originalLine.id && !currentMap.has(originalLine.id)) {
        deletedLineIds.push(originalLine.id);
      }
    });

    // Check for changed and new lines
    currentLines?.forEach(currentLine => {
      if (currentLine.id && originalMap.has(currentLine.id)) {
        // Existing line - check for changes
        const originalLine = originalMap.get(currentLine.id);
        const hasChanges = JSON.stringify(originalLine) !== JSON.stringify(currentLine);
        
        if (hasChanges) {
          changedLines.push({
            workBoqLineId: currentLine.id,
            workCategoryId: Number(currentLine.workCategory),
            taskTitle: currentLine.taskTitle,
            taskDescription: currentLine.taskDescription || "",
            quantity: Number(currentLine.quantity) || 0,
            uomId: Number(currentLine.uom),
            towerVilla: currentLine.towerVilla ? Number(currentLine.towerVilla) : null,
            floorName: currentLine.floorName || null,
            flatName: currentLine.flatName || null,
            budgetLineId: currentLine.budgetLineId ? Number(currentLine.budgetLineId) : null,
            budgetAllocation: currentLine.allocatedAmount ? Number(currentLine.allocatedAmount) : null,
            allocatedMaterials: currentLine.allocatedMaterials?.map((material: any) => ({
              materialId: Number(material.materialId),
              categoryId: Number(material.categoryId),
              allocatedQty: Number(material.quantity),
              uomId: Number(material.uomId),
            })) || [],
          });
        }
      } else if (!currentLine.id || !originalMap.has(currentLine.id)) {
        // New line
        newLines.push({
          workCategoryId: Number(currentLine.workCategory),
          taskTitle: currentLine.taskTitle,
          taskDescription: currentLine.taskDescription || "",
          quantity: Number(currentLine.quantity) || 0,
          uomId: Number(currentLine.uom),
          towerVilla: currentLine.towerVilla ? Number(currentLine.towerVilla) : null,
          floorName: currentLine.floorName || null,
          flatName: currentLine.flatName || null,
          budgetLineId: currentLine.budgetLineId ? Number(currentLine.budgetLineId) : null,
          budgetAllocation: currentLine.allocatedAmount ? Number(currentLine.allocatedAmount) : null,
          allocatedMaterials: currentLine.allocatedMaterials?.map((material: any) => ({
            materialId: Number(material.materialId),
            categoryId: Number(material.categoryId),
            allocatedQty: Number(material.quantity),
            uomId: Number(material.uomId),
          })) || [],
        });
      }
    });

    return { changedLines, newLines, deletedLineIds };
  };

  // Helper function to detect document deletions
  const detectDocumentDeletions = (originalLines: any[], currentLines: any[]) => {
    const deletedDocIds: string[] = [];

    // Create maps for easier comparison
    const originalMap = new Map();
    originalLines?.forEach(line => {
      if (line.id) {
        originalMap.set(line.id, line);
      }
    });

    const currentMap = new Map();
    currentLines?.forEach(line => {
      if (line.id) {
        currentMap.set(line.id, line);
      }
    });

    // Check for deleted documents in existing lines
    originalLines?.forEach(originalLine => {
      if (originalLine.id && currentMap.has(originalLine.id)) {
        const currentLine = currentMap.get(originalLine.id);
        const originalDocs = originalLine.uploadedDocs || [];
        const currentDocs = currentLine.uploadedDocs || [];

        // Create sets of document IDs/names for comparison
        const currentDocNames = new Set(currentDocs.map((doc: any) => doc.name || doc.fileName));
        
        originalDocs.forEach((originalDoc: any) => {
          const docName = originalDoc.name || originalDoc.fileName;
          if (docName && !currentDocNames.has(docName)) {
            // Document was deleted
            if (originalDoc.id || originalDoc.fileId) {
              deletedDocIds.push(originalDoc.id || originalDoc.fileId);
            }
          }
        });
      }
    });

    return deletedDocIds;
  };

  // Update Work BOQ function with comprehensive change detection
  const updateWorkBoq = async (data: any) => {
    try {
      setIsSubmitting(true);
      
      if (!originalData) {
        throw new Error("Original data not available for comparison");
      }

      console.log("Current data:", data);
      console.log("Original data:", originalData);

      // Detect basic field changes
      const basicChanges = detectChanges(originalData, data);
      console.log("Basic field changes:", basicChanges);

      // Detect line changes and deletions
      const lineChanges = detectLineChanges(originalData.workOrderLines || [], data.workOrderLines || []);
      console.log("Line changes:", lineChanges);

      // Detect document deletions
      const deletedDocIds = detectDocumentDeletions(originalData.workOrderLines || [], data.workOrderLines || []);
      console.log("Deleted document IDs:", deletedDocIds);

      // Build update payload - only send workBoqId + changed fields
      const payload: any = {
        workBoqId: Number(editWorkBoqId)
      };

      // Add basic field changes
      Object.keys(basicChanges).forEach(key => {
        if (key === 'completionAlertsFrom' && basicChanges[key]) {
          payload.completionAlertsFrom = parseInt(basicChanges[key]);
        } else if (key === 'siteId' && basicChanges[key]) {
          payload.siteId = parseInt(basicChanges[key]);
        } else if (key === 'projectId' && basicChanges[key]) {
          payload.projectId = parseInt(basicChanges[key]);
        } else if (key === 'contractorId' && basicChanges[key]) {
          payload.contractorId = parseInt(basicChanges[key]);
        } else if (key === 'budgetId' && basicChanges[key]) {
          payload.budgetHeaderId = parseInt(basicChanges[key]);
        } else if (key === 'materialBoqId' && basicChanges[key]) {
          payload.materialBoqId = parseInt(basicChanges[key]);
        } else if (key === 'workLevel' && basicChanges[key]) {
          payload.segmentLevel = basicChanges[key] === "Tower / Block" ? "Block" : basicChanges[key];
        } else if (key === 'approvalRequired' && basicChanges[key]) {
          payload.approvalRequired = basicChanges[key] === "true";
        } else if (key === 'approvalsData' && basicChanges[key]) {
          try {
            const approvalsData = JSON.parse(basicChanges[key]);
            const rawApprovals = approvalsData.data || [];
            payload.approvalHierarchy = rawApprovals.map((approval: any) => ({
              userId: Number(approval.userId),
              level: approval.level,
              approvalStage: approval.approvalStage
            }));
          } catch (error) {
            console.log("Error parsing approvals data:", error);
          }
        } else {
          payload[key] = basicChanges[key];
        }
      });

      // Add line changes if any
      if (lineChanges.changedLines.length > 0) {
        payload.updatedWorkBoqLines = lineChanges.changedLines;
      }

      // Add new lines if any
      if (lineChanges.newLines.length > 0) {
        payload.newWorkBoqLines = lineChanges.newLines;
      }

      // Add deleted line IDs if any
      if (lineChanges.deletedLineIds.length > 0) {
        payload.deletedLineIds = lineChanges.deletedLineIds;
      }

      // Add deleted document IDs if any
      if (deletedDocIds.length > 0) {
        payload.deletedDocIds = deletedDocIds;
      }

      // Check if there are any changes to send
      const hasChanges = Object.keys(payload).length > 1; // More than just workBoqId

      if (!hasChanges) {
        message.info("No changes detected to update");
        return;
      }

      console.log("Update payload with changes only:", JSON.stringify(payload, null, 2));

      // Handle file uploads for new/changed lines
      const files: File[] = [];
      const filesData: any[] = [];
      let fileIndex = 0;

      // Process files from new lines and changed lines
      [...(lineChanges.newLines || []), ...(lineChanges.changedLines || [])].forEach((line: any, lineIndex: number) => {
        const currentLine = data.workOrderLines?.find((wl: any) => 
          (line.workBoqLineId && wl.id === line.workBoqLineId) || 
          (!line.workBoqLineId && JSON.stringify(wl).includes(line.taskTitle))
        );

        if (currentLine?.uploadedDocs) {
          currentLine.uploadedDocs.forEach((doc: any) => {
            if (doc.originFileObj) {
              files.push(doc.originFileObj);
              filesData.push({
                order: fileIndex,
                module: "Work Order Management",
                moduleFileType: "Work BOQ Supporting Document",
                taskTitle: line.taskTitle,
                workBoqLineId: line.workBoqLineId || null
              });
              fileIndex++;
            }
          });
        }
      });

      // Always use FormData for consistency with create function
      const formData = new FormData();
      
      // Add workBoqId
      formData.append("workBoqId", payload.workBoqId.toString());
      
      // Add only changed basic fields using individual formData.append()
      Object.keys(basicChanges).forEach(key => {
        if (key === 'completionAlertsFrom' && basicChanges[key]) {
          formData.append("completionAlertsFrom", parseInt(basicChanges[key]).toString());
        } else if (key === 'siteId' && basicChanges[key]) {
          formData.append("siteId", parseInt(basicChanges[key]).toString());
        } else if (key === 'projectId' && basicChanges[key]) {
          formData.append("projectId", parseInt(basicChanges[key]).toString());
        } else if (key === 'contractorId' && basicChanges[key]) {
          formData.append("contractorId", parseInt(basicChanges[key]).toString());
        } else if (key === 'budgetId' && basicChanges[key]) {
          formData.append("budgetHeaderId", parseInt(basicChanges[key]).toString());
        } else if (key === 'materialBoqId' && basicChanges[key]) {
          formData.append("materialBoqId", parseInt(basicChanges[key]).toString());
        } else if (key === 'workLevel' && basicChanges[key]) {
          const segmentLevel = basicChanges[key] === "Tower / Block" ? "Block" : basicChanges[key];
          formData.append("segmentLevel", segmentLevel);
        } else if (key === 'approvalRequired' && basicChanges[key]) {
          formData.append("approvalRequired", (basicChanges[key] === "true").toString());
        } else if (key === 'approvalsData' && basicChanges[key]) {
          try {
            const approvalsData = JSON.parse(basicChanges[key]);
            const rawApprovals = approvalsData.data || [];
            const approvalHierarchy = rawApprovals.map((approval: any) => ({
              userId: Number(approval.userId),
              level: approval.level,
              approvalStage: approval.approvalStage
            }));
            formData.append("approvalHierarchy", JSON.stringify(approvalHierarchy));
          } catch (error) {
            console.log("Error parsing approvals data:", error);
          }
        } else if (key === 'boqTitle' && basicChanges[key]) {
          formData.append("boqTitle", basicChanges[key]);
        } else if (key === 'boqDescription' && basicChanges[key]) {
          formData.append("boqDescription", basicChanges[key]);
        }
      });

      // Add line changes if any
      if (lineChanges.changedLines.length > 0) {
        formData.append("updatedWorkBoqLines", JSON.stringify(lineChanges.changedLines));
      }

      // Add new lines if any
      if (lineChanges.newLines.length > 0) {
        formData.append("workBoqLines", JSON.stringify(lineChanges.newLines));
      }

      // Add deleted line IDs if any
      if (lineChanges.deletedLineIds.length > 0) {
        formData.append("deletedLineIds", JSON.stringify(lineChanges.deletedLineIds));
      }

      // Add deleted document IDs if any
      if (deletedDocIds.length > 0) {
        formData.append("deletedDocIds", JSON.stringify(deletedDocIds));
      }
      
      // Add file metadata if there are files
      if (filesData.length > 0) {
        formData.append("filesData", JSON.stringify(filesData));
      }
      
      // Add all files
      files.forEach((file) => {
        formData.append("files", file);
      });

      console.log("FormData contents:");
      // Use forEach instead of for...of to avoid TypeScript iteration issues
      const entries = Array.from(formData.entries());
      entries.forEach(([key, value]) => {
        console.log(key, value);
      });

      // Call update API with FormData
      const response: any = await workBoqApis.updateWorkBoq(editWorkBoqId, formData);
      
      if (response && response.status) {
        message.success("Work BOQ updated successfully!");
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response?.data?.message || "Work BOQ updated successfully",
          showConfirmButton: true,
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/work-boq-list");
        });
      } else {
        throw new Error(response.message || "Failed to update Work BOQ");
      }
    } catch (error: any) {
      console.error("Error updating Work BOQ:", error);
      message.error("Failed to update Work BOQ");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to update Work BOQ. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitWorkBoq = async (data: any, status: string = "Submitted") => {
    try {
      setIsSubmitting(true);

      // Parse and transform approvals data to required format
      let approvalHierarchy = [];
      try {
        if (data.approvalsData) {
          const approvalsData = JSON.parse(data.approvalsData);
          const rawApprovals = approvalsData.data || [];
          
          // Transform to required format: { userId, level, approvalStage }
          approvalHierarchy = rawApprovals.map((approval: any) => ({
            userId: Number(approval.userId),
            level: approval.level,
            approvalStage: approval.approvalStage
          }));
        }
      } catch (error) {
        console.log("Error parsing approvals data:", error);
      }
      console.log("data", data);

      // Prepare files and filesData arrays
      const files: File[] = [];
      const filesData: any[] = [];
      let fileIndex = 0;

      // Transform Work BOQ Lines to match API format
      const workBoqLines =
        data.workOrderLines?.map((line: any, index: number) => {
          // Process uploaded documents for this line
          const processedDocs =
            line.uploadedDocs?.map((doc: any) => {
              if (doc.originFileObj) {
                // Add actual file to files array
                files.push(doc.originFileObj);

                // Add file metadata to filesData array
                filesData.push({
                  order: fileIndex,
                  module: "Work Order Management",
                  moduleFileType: "Work BOQ Supporting Document",
                  taskTitle: line.taskTitle,
                });

                fileIndex++;
              }
              return {
                name: doc.name,
                size: doc.size,
                type: doc.type,
                fileIndex: fileIndex - 1, // Reference to the file index
              };
            }) || [];

          return {
            workCategoryId: Number(line.workCategory),
            taskTitle: line.taskTitle,
            taskDescription: line.taskDescription || "",
            quantity: Number(line.quantity) || 0,
            uomId: Number(line.uom),
            towerVilla: line.towerVilla ? Number(line.towerVilla) : null,
            floorName: line.floorName || null,
            flatName: line.flatName || null,
            budgetLineId: line.budgetLineId ? Number(line.budgetLineId) : null,
            budgetAllocation: line.allocatedAmount ? Number(line.allocatedAmount) : null,
            allocatedMaterials:
              line.allocatedMaterials?.map((material: any) => ({
                materialId: Number(material.materialId),
                categoryId: Number(material.categoryId),
                allocatedQty: Number(material.quantity),
                uomId: Number(material.uomId),
              })) || [],
            // uploadedDocs: processedDocs
          };
        }) || [];

      // Prepare payload for API submission according to the specified format
      const payload = {
        boqTitle: data.boqTitle,
        boqDescription: data.boqDescription || "",
        siteId: parseInt(data.siteId),
        projectId: data.projectId ? parseInt(data.projectId) : null,
        contractorId: data.contractorId ? parseInt(data.contractorId) : null,
        budgetHeaderId: data.budgetId ? parseInt(data.budgetId) : null,
        completionAlertsFrom: data.completionAlertsFrom
          ? parseInt(data.completionAlertsFrom)
          : null,
        segmentLevel:
          data.workLevel && data.workLevel === "Tower / Block"
            ? "Block"
            : data.workLevel,
        approvalRequired: data.approvalRequired === "true",
        materialBoqId: data.materialBoqId && Number(data.materialBoqId),
        approvalHierarchy:
          data.approvalRequired === "true" ? approvalHierarchy : [],
        status: status,
        workBoqLines: workBoqLines,
        filesData: filesData,
      };

      console.log("Work BOQ Submission Payload:", payload);
      console.log("Files to upload:", files);
      console.log("Files metadata:", filesData);

      // Create FormData for file upload
      const formData = new FormData();

      // Add each field of the payload directly to FormData
      formData.append("boqTitle", payload.boqTitle);
      formData.append("boqDescription", payload.boqDescription);
      formData.append("siteId", payload.siteId.toString());
      if (payload.projectId) {
        formData.append("projectId", payload.projectId.toString());
      }
      if (payload.contractorId) {
        formData.append("contractorId", payload.contractorId.toString());
      }
      if (payload.budgetHeaderId) {
        formData.append("budgetHeaderId", payload.budgetHeaderId.toString());
      }
      if (payload.completionAlertsFrom) {
        formData.append(
          "completionAlertsFrom",
          payload.completionAlertsFrom.toString()
        );
      }
      formData.append("segmentLevel", payload.segmentLevel);
      formData.append("approvalRequired", payload.approvalRequired.toString());
      formData.append(
        "approvalHierarchy",
        JSON.stringify(payload.approvalHierarchy)
      );
      formData.append("status", payload.status);
      formData.append("workBoqLines", JSON.stringify(payload.workBoqLines));
      formData.append("filesData", JSON.stringify(payload.filesData));
      formData.append("materialBoqId", payload.materialBoqId.toString());
      // Add all files
      files.forEach((file, index) => {
        formData.append("files", file);
      });

      // Call the actual API with FormData
      const response: any = await workBoqApis.createWorkBoq(formData);

      if (response && response.status) {
        message.success("Work BOQ created successfully!");
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response?.data?.message,
          showConfirmButton: true,
          confirmButtonText: "OK",
        }).then(() => {
          navigate("/work-boq-list");
        });
      } else {
        throw new Error(response.message || "Failed to create Work BOQ");
      }
    } catch (error: any) {
      console.error("Error submitting Work BOQ:", error);
      message.error("Failed to create Work BOQ");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to create Work BOQ. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  return (
    <>
      <Head title="Work BOQ Preview" />
      <div className="content clearfix">
        <form
          onSubmit={handleSubmit((data) => submitWorkBoq(data, "Submitted"))}
        >
          <AntCollapse defaultActiveKey={["1", "2"]}>
            <Panel
              header={
                <div className="d-flex align-items-center justify-content-between">
                  <>General Information</>
                </div>
              }
              key="1"
            >
              <Col className="justify-start w-100 flex-wrap mt-1">
                <div className="w-30 border-end">
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">BOQ Title</div>
                    <div className="data-value w-70">
                      {formData.boqTitle || "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Site</div>
                    <div className="data-value w-70">
                      {formData.siteLabel || formData.siteId || "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Project</div>
                    <div className="data-value w-70">
                      {formData.projectLabel || formData.projectId || "--"}
                    </div>
                  </div>
                  {/* <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Validation</div>
                    <div className="data-value w-70">
                      <ValidationSummaryContent />
                    </div>
                  </div> */}
                </div>
                <div className="w-30 border-end paddingLeft10">
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Contractor</div>
                    <div className="data-value w-70">
                      {formData.contractorLabel ||
                        formData.contractorId ||
                        "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Material BOQ</div>
                    <div className="data-value w-70">
                      {formData.materialBoqLabel ||
                        formData.materialBoqId ||
                        "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Budget</div>
                    <div className="data-value w-70">
                      {formData.budgetLabel ||
                        formData.budgetId ||
                        "--"}
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-30">Work Level</div>
                    <div className="data-value w-70">
                      <Badge color="outline-primary">
                        {workLevelLabels[formData.workLevel] ||
                          formData.workLevel ||
                          "--"}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div className="w-30 paddingLeft10">
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-40">Description</div>
                    <div className="data-value w-70">
                      <Popover
                        content={
                          <div>
                            <p>{formData.boqDescription}</p>
                          </div>
                        }
                        title={"BOQ Description"}
                      >
                        {formData.boqDescription?.length > 30 ? (
                          <span
                            style={{
                              color: "#2263b3",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            {formData.boqDescription.substring(0, 30)}...
                          </span>
                        ) : (
                          formData.boqDescription || "--"
                        )}
                      </Popover>
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-40">Completion Alert</div>
                    <div className="data-value w-70">
                      <Badge color="outline-info">{completionAlertLabel}</Badge>
                    </div>
                  </div>
                  <div className="data-col align-center pt-1">
                    <div className="data-label w-40">Approval Required</div>
                    <div className="data-value w-70">
                      <ApprovalTimelineContent />
                    </div>
                  </div>
                </div>
              </Col>
            </Panel>

            <Panel header="Work BOQ Lines" key="2">
              <div className="table-responsive poTable">
                <div
                  style={{
                    width: screenSize - 170 + "px",
                    margin: "auto",
                  }}
                >
                  {!formData.workOrderLines ||
                  formData.workOrderLines.length === 0 ? (
                    <div className="text-center py-5">
                      <Empty description="No Work BOQ Lines added" />
                    </div>
                  ) : (
                    <Table
                      className="customTable"
                      columns={columns}
                      dataSource={formData.workOrderLines}
                      scroll={{ x: 1000 }}
                      expandable={{
                        expandedRowRender: (record: any) => (
                          <div className="expandableBody">
                            <div style={{ flex: 1, textAlign: "left" }}>
                              <div className="mb-3">
                                <strong>Task Description:</strong>
                                <p
                                  style={{
                                    margin: 0,
                                    marginTop: 5,
                                    padding: "8px",
                                    backgroundColor: "#f5f5f5",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {record.taskDescription ||
                                    "No description provided"}
                                </p>
                              </div>

                              <div className="mb-3">
                                {record.allocatedMaterials?.length > 0 ? (
                                  <div>
                                    <strong>
                                      Allocated Materials (
                                      {record.allocatedMaterials.length}):
                                    </strong>
                                    <div className="mt-2">
                                      <Table
                                        className="customTable"
                                        columns={[
                                          {
                                            title: "Material",
                                            key: "material",
                                            width: "40%",
                                            render: (
                                              _,
                                              record: AllocatedMaterial
                                            ) => (
                                              <div>
                                                <div className="fw-medium">
                                                  {record.materialName}
                                                </div>
                                                {record.category && (
                                                  <Tag color="blue">
                                                    {record.category}
                                                  </Tag>
                                                )}
                                              </div>
                                            ),
                                          },
                                          {
                                            title: "Quantity",
                                            key: "quantity",
                                            width: "20%",
                                            render: (
                                              _,
                                              record: AllocatedMaterial
                                            ) => (
                                              <span>
                                                {record.quantity}{" "}
                                                {record.uomName || record.uom}
                                              </span>
                                            ),
                                          },
                                          {
                                            title: "UOM",
                                            dataIndex: "uom",
                                            key: "uom",
                                            width: "15%",
                                            render: (
                                              uom: string,
                                              record: AllocatedMaterial
                                            ) => (
                                              <Tag color="geekblue">
                                                {record.uomName || uom}
                                              </Tag>
                                            ),
                                          },
                                        ]}
                                        dataSource={record.allocatedMaterials}
                                        rowKey="id"
                                        pagination={false}
                                        size="small"
                                        bordered
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-muted">
                                    <Icon name="info" className="me-2" />
                                    No materials allocated
                                  </div>
                                )}
                              </div>

                              {record.uploadedDocs?.length > 0 && (
                                <div className="mb-3">
                                  <strong>
                                    Uploaded Documents (
                                    {record.uploadedDocs.length}):
                                  </strong>
                                  <div style={{ marginTop: 8 }}>
                                    {record.uploadedDocs.map(
                                      (doc: any, idx: number) => (
                                        <Tag
                                          key={idx}
                                          color="blue"
                                          style={{ margin: "2px" }}
                                          icon={<Icon name="file-docs" />}
                                        >
                                          {doc.name}
                                        </Tag>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                              {(record.towerVilla ||
                                record.floorName ||
                                record.flatName) && (
                                <div className="mb-3">
                                  <strong>Location Details:</strong>
                                  <div style={{ marginTop: 5 }}>
                                    {record.towerVilla && (
                                      <Tag color="green">
                                        Tower/Villa: {record.towerVilla}
                                      </Tag>
                                    )}
                                    {record.floorName && (
                                      <Tag color="orange">
                                        Floor: {record.floorName}
                                      </Tag>
                                    )}
                                    {record.flatName && (
                                      <Tag color="purple">
                                        Flat: {record.flatName}
                                      </Tag>
                                    )}
                                  </div>
                                </div>
                              )}

                              {record.budgetLineName && (
                                <div className="mb-3">
                                  <strong>Budget Allocation:</strong>
                                  <div style={{ marginTop: 5 }}>
                                    <Tag color="blue">
                                      Budget Line: {record.budgetLineName}
                                    </Tag>
                                    <Tag color="cyan">
                                      Allocated Amount: ₹{formatDecimal(record.allocatedAmount || 0)}
                                    </Tag>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        ),
                      }}
                      bordered
                      size="small"
                      pagination={false}
                    />
                  )}
                </div>

                {formData.workOrderLines?.length > 0 && (
                  <Row style={{ margin: "20px" }}>
                    <Col className="justify-end">
                      <div className="w-40">
                        <div className="d-flex justify-content-between">
                          <div className="w-50 justify-end">
                            <strong>Total Work Lines</strong>
                          </div>
                          <span className="r_align">
                            {formData.workOrderLines.length}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div className="w-50 justify-end">
                            <strong>Total Quantity</strong>
                          </div>
                          <span className="r_align">
                            {formatDecimal(
                              formData.workOrderLines.reduce(
                                (total: number, line: any) =>
                                  total + (Number(line.quantity) || 0),
                                0
                              )
                            )}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div className="w-50 justify-end">
                            <strong>Work Categories</strong>
                          </div>
                          <span className="r_align">
                            {
                              new Set(
                                formData.workOrderLines.map(
                                  (line: any) => line.workCategory
                                )
                              ).size
                            }
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <div className="w-50 justify-end">
                            <strong>Total Budget Allocation</strong>
                          </div>
                          <span className="r_align">
                            ₹{formatDecimal(
                              formData.workOrderLines.reduce(
                                (total: number, line: any) =>
                                  total + (Number(line.allocatedAmount) || 0),
                                0
                              )
                            )}
                          </span>
                        </div>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </Panel>
          </AntCollapse>

          <div className="actions clearfix">
            <ul>
              <li>
                {isSubmitting ? (
                  <Button color="primary" type="button">
                    <Spinner size="sm" color="light" />
                  </Button>
                ) : isEditMode ? (
                  // Edit Mode - Show Update button only
                  <Button
                    color="primary"
                    type="button"
                    onClick={() =>
                      handleSubmit((data) => updateWorkBoq(data))()
                    }
                    disabled={
                      !formData.boqTitle ||
                      !formData.siteId ||
                      !formData.workLevel ||
                      !formData.workOrderLines?.length
                    }
                  >
                    Update Work BOQ
                  </Button>
                ) : (
                  // Create Mode - Show Save as Draft and Submit buttons
                  <>
                    <Button
                      color="success"
                      type="button"
                      onClick={() =>
                        handleSubmit((data) => submitWorkBoq(data, "Draft"))()
                      }
                      disabled={
                        !formData.boqTitle ||
                        !formData.siteId ||
                        // !formData.materialBoqId ||
                        !formData.workLevel ||
                        !formData.workOrderLines?.length ||
                        !formData.approvalRequired
                      }
                      style={{ marginRight: "10px" }}
                    >
                      Save as Draft
                    </Button>
                    <Button
                      color="primary"
                      type="submit"
                      disabled={
                        !formData.boqTitle ||
                        !formData.siteId ||
                        // !formData.materialBoqId ||
                        !formData.workLevel ||
                        !formData.workOrderLines?.length ||
                        !formData.approvalRequired ||
                        (formData.approvalRequired === "true" &&
                          (() => {
                            try {
                              if (!formData.approvalsData) {
                                console.log("No approvals data found");
                                return true; // Block if no approvals data
                              }
                              const approvalsData = JSON.parse(formData.approvalsData);
                              const hasApprovals = approvalsData?.data && approvalsData.data.length > 0;
                              console.log("Approvals validation check:", {
                                formData: formData.approvalsData,
                                parsed: approvalsData,
                                hasApprovals,
                                shouldBlock: !hasApprovals
                              });
                              return !hasApprovals; // Block if no valid approvals
                            } catch (error) {
                              console.error("Error parsing approvals data for validation:", error);
                              return true; // Block on error
                            }
                          })())
                      }
                    >
                      Submit Work BOQ
                    </Button>
                  </>
                )}
              </li>
              <li>
                <Button
                  color="primary"
                  onClick={() => {
                    props.prev();
                  }}
                  className="btn-dim btn btn-secondary"
                >
                  Previous
                </Button>
              </li>
            </ul>
          </div>
        </form>
      </div>
    </>
  );
};

export default WorkBoqPreview;
